import { escapeHtml, highlightDisplayName } from "./search.js";

export function formatDate(date) {
  if (!date) return "n/a";

  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export function formatFullDate(date) {
  if (!date) return "n/a";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function applySorting(files, doc = document) {
  const sorted = [...files];
  const sortByEl = doc.getElementById("sort-by");
  const sortOrderEl = doc.getElementById("sort-order");

  const sortBy = sortByEl ? sortByEl.value : "created_at";
  const sortOrder = sortOrderEl ? sortOrderEl.value : "desc";

  switch (sortBy) {
    case "created_at":
      sorted.sort((a, b) =>
        sortOrder === "desc" ? b.created - a.created : a.created - b.created,
      );
      break;
    case "filename":
      sorted.sort((a, b) => {
        const comparison = a.name.localeCompare(b.name);
        return sortOrder === "desc" ? -comparison : comparison;
      });
      break;
    default:
      break;
  }

  return sorted;
}

function escapeJsStringForOnclick(value) {
  return String(value).replace(/\\/g, "\\\\").replace(/'/g, "\\'");
}

export function getFileActions(file) {
  const safeId = escapeJsStringForOnclick(file.id);
  switch (file.status) {
    case "queued":
    case "uploading":
    case "transcribing":
    case "processing":
      return [
        {
          text: "Cancel",
          class: "danger",
          onclick: `cancelFile('${safeId}')`,
        },
      ];
    case "failed":
      return [
        {
          text: "Retry",
          class: "primary",
          onclick: `retryFile('${safeId}')`,
        },
        {
          text: "Delete",
          class: "danger-muted",
          onclick: `deleteFile('${safeId}')`,
        },
      ];
    case "complete":
      return [
        {
          text: "Download",
          class: "",
          onclick: `downloadFile('${safeId}')`,
        },
        {
          text: "Delete",
          class: "danger-muted",
          onclick: `deleteFile('${safeId}')`,
        },
      ];
    default:
      return [];
  }
}

export function filterFilesByStatus(files, status) {
  switch (status) {
    case "in_progress":
      return files.filter((f) =>
        ["queued", "uploading", "transcribing", "processing"].includes(
          f.status,
        ),
      );
    case "completed":
      return files.filter((f) => f.status === "complete");
    case "failed":
      return files.filter((f) => f.status === "failed");
    default:
      return files;
  }
}

export function createFileRowHTML(file, options) {
  const {
    searchMode,
    searchTerm,
    contentHits,
    contentSearchMinLength,
    escapeAttributeValue,
    getDisplayName,
    numberFormatter,
  } = options;

  const actions = getFileActions(file);

  let metaInfo = "";
  if (file.status === "queued") {
    metaInfo = `<div class="progress-bar progress-bar--indeterminate" data-status="queued"></div>`;
  } else if (file.status === "transcribing") {
    if (file.progress != null && file.progress >= 0 && file.progress <= 100) {
      metaInfo = `<div class="progress-bar progress-bar--transcribing"><div class="progress-fill--transcribing" style="width: ${file.progress}%"></div></div>`;
    } else {
      metaInfo = `<div class="progress-bar progress-bar--indeterminate" data-status="transcribing"></div>`;
    }
  } else if (file.status !== "complete" && file.progress < 100) {
    metaInfo = `<div class="progress-bar"><div class="progress-fill" style="width: ${file.progress}%"></div></div>`;
  } else if (file.status === "complete") {
    metaInfo = `created: ${formatFullDate(file.created)}`;
  } else if (file.status === "failed") {
    metaInfo = `failed ${formatDate(file.created)}`;
  } else {
    metaInfo = `uploaded ${formatDate(file.created)}`;
  }

  let snippetHtml = "";
  if (
    searchMode === "content" &&
    searchTerm.length >= contentSearchMinLength &&
    contentHits.has(file.name)
  ) {
    const hit = contentHits.get(file.name);
    snippetHtml = `<div class="file-snippet">${hit.snippet}</div>`;
  }

  const attributes = ['class="file-row"', `data-file-id="${file.id}"`];
  if (file.status === "complete") {
    const safeFilename = escapeAttributeValue(file.name);
    attributes.push(`data-filename="${safeFilename}"`);
    attributes.push('onclick="handleFileRowClick(event)"');
  }

  const displayName = getDisplayName(file.name);
  const nameHtml =
    searchMode === "files" && searchTerm
      ? highlightDisplayName(displayName, searchTerm)
      : escapeHtml(displayName);

  return `
                <div ${attributes.join(" ")}>
                    <div class="file-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14,2 14,8 20,8"/>
                        </svg>
                    </div>
                    <div class="file-info">
                        <div class="file-name">${nameHtml}</div>
                        <div class="file-meta">${metaInfo}</div>
                        ${snippetHtml}
                    </div>
                    <div class="status-pill status-${file.status}">${file.status}</div>
                    <div class="file-actions" onclick="event.stopPropagation()">
                        ${actions
                          .map(
                            (action) =>
                              `<button class="action-btn ${action.class}" onclick="${action.onclick}">${action.text}</button>`,
                          )
                          .join("")}
                    </div>
                </div>
            `;
}
